/**
 * CompareInvoicesModal — side-by-side view of an existing invoice vs a newly
 * uploaded one. Used by the bulk upload flow when a duplicate (hard or soft)
 * is detected. Cells with different values are highlighted so the user can
 * decide which record to keep.
 *
 * Actions offered at the footer (delegated to the parent):
 *   - "Editar esta" on each column (opens the edit modal for that invoice)
 *   - "Actualizar con la última" (replace-with endpoint)
 *   - "Mantener ambas" (dismiss the compare view)
 *   - "Borrar duplicado nueva" (delete the newly uploaded one)
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Check, ArrowRightLeft } from "lucide-react";
import { formatDateShort } from "@/lib/format";

const money = (v, cur) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return `${n.toFixed(2)} ${cur || "EUR"}`;
};

function CompareRow({ label, existingValue, newValue, suppressHighlight }) {
  const differ = !suppressHighlight &&
    String(existingValue ?? "").trim() !== String(newValue ?? "").trim() &&
    !(existingValue == null && newValue == null);
  return (
    <div className="grid grid-cols-[140px_1fr_1fr] gap-2 py-1.5 border-b border-[var(--border-color)] text-xs">
      <div className="text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div
        className={
          differ
            ? "bg-[var(--status-warning-bg)] text-[var(--status-warning)] px-1.5 rounded font-mono-num"
            : "font-mono-num"
        }
      >
        {existingValue || <span className="text-[var(--text-muted)]">—</span>}
      </div>
      <div
        className={
          differ
            ? "bg-[var(--status-warning-bg)] text-[var(--status-warning)] px-1.5 rounded font-mono-num"
            : "font-mono-num"
        }
      >
        {newValue || <span className="text-[var(--text-muted)]">—</span>}
      </div>
    </div>
  );
}

export default function CompareInvoicesModal({
  open,
  onOpenChange,
  existing,
  fresh,
  lang,
  busy,
  onEditExisting,
  onEditNew,
  onReplace,
  onKeepBoth,
  onDeleteNew,
  onDismiss,
}) {
  if (!existing) return null;
  // iter 62 — hard-duplicate (same SHA-256): the new file was rejected before
  // OCR, so `fresh` is null. Render as a single-column view where the "New"
  // column shows "Idéntica (archivo rechazado)". The user can still edit or
  // dismiss the existing invoice from here.
  const hardDup = !fresh;
  const cur = existing.currency || fresh?.currency || "EUR";
  const rowCount = Math.max(
    (existing.line_items || []).length,
    (fresh?.line_items || []).length,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-invoices-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft size={16} />
            {lang === "es" ? "Comparar facturas" : "Compare invoices"}
          </DialogTitle>
        </DialogHeader>

        {/* Header row: identifies each column */}
        <div className="grid grid-cols-[140px_1fr_1fr] gap-2 pt-2 pb-1 font-semibold text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          <div />
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-muted)]" />
            {lang === "es" ? "Existente" : "Existing"}
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)]" />
            {hardDup
              ? (lang === "es" ? "Nueva subida (rechazada)" : "New upload (rejected)")
              : (lang === "es" ? "Nueva subida" : "New upload")}
          </div>
        </div>

        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Fecha" : "Date"} existingValue={formatDateShort(existing.date)} newValue={hardDup ? (lang === "es" ? "Idéntica (archivo rechazado)" : "Identical (file rejected)") : formatDateShort(fresh.date)} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Número" : "Number"} existingValue={existing.invoice_number} newValue={hardDup ? "—" : fresh.invoice_number} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Proveedor" : "Vendor"} existingValue={existing.vendor} newValue={hardDup ? "—" : fresh.vendor} />
        <CompareRow suppressHighlight={hardDup} label="CIF" existingValue={existing.vendor_tax_id} newValue={hardDup ? "—" : fresh.vendor_tax_id} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Subtotal" : "Subtotal"} existingValue={money(existing.subtotal, cur)} newValue={hardDup ? "—" : money(fresh.subtotal, cur)} />
        <CompareRow suppressHighlight={hardDup} label="IVA" existingValue={money(existing.tax, cur)} newValue={hardDup ? "—" : money(fresh.tax, cur)} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Total" : "Total"} existingValue={money(existing.amount ?? existing.total, cur)} newValue={hardDup ? "—" : money(fresh.amount ?? fresh.total, cur)} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Categoría" : "Category"} existingValue={existing.category} newValue={hardDup ? "—" : fresh.category} />
        <CompareRow suppressHighlight={hardDup} label={lang === "es" ? "Notas" : "Notes"} existingValue={existing.notes} newValue={hardDup ? "—" : fresh.notes} />

        {rowCount > 0 && (
          <div className="mt-3">
            <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
              {lang === "es" ? `Líneas (${rowCount})` : `Line items (${rowCount})`}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="border border-[var(--border-color)] rounded p-2 bg-[var(--surface)] max-h-40 overflow-y-auto">
                {(existing.line_items || []).length === 0 ? (
                  <span className="text-[var(--text-muted)]">—</span>
                ) : (
                  (existing.line_items || []).map((li, i) => (
                    <div key={li._uid || li.id || `${i}:${li.name || ""}:${li.qty ?? ""}:${li.total ?? ""}`} className="font-mono-num truncate">
                      {li.qty ?? "—"} × {li.name || "—"} · {money(li.total, cur)}
                    </div>
                  ))
                )}
              </div>
              <div className="border border-[var(--border-color)] rounded p-2 bg-[var(--surface)] max-h-40 overflow-y-auto">
                {hardDup ? (
                  <span className="text-[var(--text-muted)]">
                    {lang === "es" ? "Idéntica (archivo rechazado)" : "Identical (file rejected)"}
                  </span>
                ) : (fresh.line_items || []).length === 0 ? (
                  <span className="text-[var(--text-muted)]">—</span>
                ) : (
                  (fresh.line_items || []).map((li, i) => (
                    <div key={li._uid || li.id || `${i}:${li.name || ""}:${li.qty ?? ""}:${li.total ?? ""}`} className="font-mono-num truncate">
                      {li.qty ?? "—"} × {li.name || "—"} · {money(li.total, cur)}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onEditExisting}
            disabled={busy}
            data-testid="compare-edit-existing-btn"
            className="text-xs"
          >
            <Pencil size={12} className="mr-1" />
            {lang === "es" ? "Editar existente" : "Edit existing"}
          </Button>
          {!hardDup && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onEditNew}
                disabled={busy}
                data-testid="compare-edit-new-btn"
                className="text-xs"
              >
                <Pencil size={12} className="mr-1" />
                {lang === "es" ? "Editar nueva" : "Edit new"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onReplace}
                disabled={busy}
                data-testid="compare-replace-btn"
                className="text-xs border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10"
              >
                <Check size={12} className="mr-1" />
                {lang === "es" ? "Actualizar con la nueva" : "Update with new"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDeleteNew}
                disabled={busy}
                data-testid="compare-delete-new-btn"
                className="text-xs text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
              >
                <Trash2 size={12} className="mr-1" />
                {lang === "es" ? "Borrar nueva" : "Delete new"}
              </Button>
            </>
          )}
          {hardDup && onDismiss && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
              disabled={busy}
              data-testid="compare-dismiss-btn"
              className="text-xs text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
            >
              <Trash2 size={12} className="mr-1" />
              {lang === "es" ? "Descartar" : "Dismiss"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onKeepBoth}
            disabled={busy}
            data-testid="compare-keep-both-btn"
            className="text-xs"
          >
            {hardDup
              ? (lang === "es" ? "Cerrar" : "Close")
              : (lang === "es" ? "Mantener ambas" : "Keep both")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
